import type { Surface } from "../domain";

export const CREW_SWIPE_THRESHOLD_PX = 48;
export const MANAGER_DRAFT_MAX_LENGTH = 16_000;

const continuityVersion = 1;
const maxCrewEntries = 256;
const maxScrollOffset = 10_000_000;
const horizontalIntentRatio = 1.2;
const surfaces: Surface[] = ["pulse", "manager", "board"];
const surfaceSet = new Set<Surface>(surfaces);

export interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const unavailableSessionStorage: SessionStorageLike = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

export function browserSessionStorage(): SessionStorageLike {
  if (typeof window === "undefined") return unavailableSessionStorage;
  try {
    return window.sessionStorage;
  } catch {
    return unavailableSessionStorage;
  }
}

interface CrewContinuityEntry {
  crewId: string;
  lastSurface: Surface;
  scroll: Partial<Record<Surface, number>>;
}

interface CrewContinuityState {
  version: typeof continuityVersion;
  crews: CrewContinuityEntry[];
}

export type CrewHopDirection = -1 | 1;

function continuityStorageKey(sessionId: string) {
  return `forge-remote:crew-continuity:v${continuityVersion}:${encodeURIComponent(sessionId)}`;
}

export function managerDraftStorageKey(sessionId: string, crewId: string) {
  return `forge-remote:manager-draft:v1:${encodeURIComponent(sessionId)}:${encodeURIComponent(crewId)}`;
}

function emptyState(): CrewContinuityState {
  return { version: continuityVersion, crews: [] };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasOnlyKeys(value: Record<string, unknown>, allowed: readonly string[]) {
  return Object.keys(value).every((key) => allowed.includes(key));
}

function isSurface(value: unknown): value is Surface {
  return typeof value === "string" && surfaceSet.has(value as Surface);
}

function isScrollRecord(value: unknown): value is Partial<Record<Surface, number>> {
  if (!isPlainObject(value) || !hasOnlyKeys(value, surfaces)) return false;
  return Object.values(value).every((offset) =>
    typeof offset === "number"
    && Number.isFinite(offset)
    && offset >= 0
    && offset <= maxScrollOffset);
}

function parseState(serialized: string | null): CrewContinuityState | null {
  if (!serialized) return null;
  try {
    const value: unknown = JSON.parse(serialized);
    if (!isPlainObject(value)
        || !hasOnlyKeys(value, ["version", "crews"])
        || value.version !== continuityVersion
        || !Array.isArray(value.crews)
        || value.crews.length > maxCrewEntries) return null;

    const seenCrewIds = new Set<string>();
    for (const entry of value.crews) {
      if (!isPlainObject(entry)
          || !hasOnlyKeys(entry, ["crewId", "lastSurface", "scroll"])
          || typeof entry.crewId !== "string"
          || !entry.crewId
          || seenCrewIds.has(entry.crewId)
          || !isSurface(entry.lastSurface)
          || !isScrollRecord(entry.scroll)) return null;
      seenCrewIds.add(entry.crewId);
    }

    return value as unknown as CrewContinuityState;
  } catch {
    return null;
  }
}

function readState(storage: SessionStorageLike, sessionId: string) {
  if (!sessionId) return emptyState();
  try {
    return parseState(storage.getItem(continuityStorageKey(sessionId))) ?? emptyState();
  } catch {
    return emptyState();
  }
}

function writeState(storage: SessionStorageLike, sessionId: string, state: CrewContinuityState) {
  if (!sessionId) return;
  try {
    storage.setItem(continuityStorageKey(sessionId), JSON.stringify(state));
  } catch {
    // Continuity is an optional client convenience; navigation remains URL-driven.
  }
}

function updateCrewEntry(
  storage: SessionStorageLike,
  sessionId: string,
  crewId: string,
  update: (entry: CrewContinuityEntry) => CrewContinuityEntry,
) {
  if (!crewId) return;
  const state = readState(storage, sessionId);
  const existingIndex = state.crews.findIndex((entry) => entry.crewId === crewId);
  const existing = existingIndex >= 0
    ? state.crews[existingIndex]
    : { crewId, lastSurface: "pulse" as const, scroll: {} };
  const nextEntry = update(existing);
  const crews = existingIndex >= 0
    ? state.crews.map((entry, index) => index === existingIndex ? nextEntry : entry)
    : [...state.crews, nextEntry].slice(-maxCrewEntries);
  writeState(storage, sessionId, { version: continuityVersion, crews });
}

export function rememberCrewSurface(
  storage: SessionStorageLike,
  sessionId: string,
  crewId: string,
  surface: Surface,
) {
  updateCrewEntry(storage, sessionId, crewId, (entry) => ({ ...entry, lastSurface: surface }));
}

export function lastCrewSurface(
  storage: SessionStorageLike,
  sessionId: string,
  crewId: string,
  fallback: Surface = "pulse",
) {
  return readState(storage, sessionId).crews.find((entry) => entry.crewId === crewId)?.lastSurface ?? fallback;
}

export function rememberCrewScroll(
  storage: SessionStorageLike,
  sessionId: string,
  crewId: string,
  surface: Surface,
  offset: number,
) {
  if (!Number.isFinite(offset)) return;
  const boundedOffset = Math.min(maxScrollOffset, Math.max(0, Math.round(offset)));
  updateCrewEntry(storage, sessionId, crewId, (entry) => ({
    ...entry,
    scroll: { ...entry.scroll, [surface]: boundedOffset },
  }));
}

export function crewScroll(
  storage: SessionStorageLike,
  sessionId: string,
  crewId: string,
  surface: Surface,
) {
  return readState(storage, sessionId).crews.find((entry) => entry.crewId === crewId)?.scroll[surface] ?? 0;
}

export function readManagerDraft(storage: SessionStorageLike, sessionId: string, crewId: string) {
  if (!sessionId || !crewId) return "";
  try {
    const value = storage.getItem(managerDraftStorageKey(sessionId, crewId)) ?? "";
    return value.length <= MANAGER_DRAFT_MAX_LENGTH ? value : "";
  } catch {
    return "";
  }
}

export function writeManagerDraft(storage: SessionStorageLike, sessionId: string, crewId: string, draft: string) {
  if (!sessionId || !crewId || draft.length > MANAGER_DRAFT_MAX_LENGTH) return;
  try {
    const key = managerDraftStorageKey(sessionId, crewId);
    if (draft) storage.setItem(key, draft);
    else storage.removeItem(key);
  } catch {
    // Draft persistence is best-effort and never widens command authority.
  }
}

export function hasManagerDraft(storage: SessionStorageLike, sessionId: string, crewId: string) {
  return readManagerDraft(storage, sessionId, crewId).trim().length > 0;
}

export function adjacentCrewId(
  crewIdsInProjectionOrder: readonly string[],
  currentCrewId: string,
  direction: CrewHopDirection,
) {
  if (crewIdsInProjectionOrder.length === 0) return null;
  const currentIndex = crewIdsInProjectionOrder.indexOf(currentCrewId);
  if (currentIndex < 0) return null;
  const nextIndex = (currentIndex + direction + crewIdsInProjectionOrder.length) % crewIdsInProjectionOrder.length;
  return crewIdsInProjectionOrder[nextIndex];
}

export function crewSwipeDirection(
  start: { x: number; y: number },
  end: { x: number; y: number },
): CrewHopDirection | null {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const horizontalDistance = Math.abs(deltaX);
  if (horizontalDistance < CREW_SWIPE_THRESHOLD_PX
      || horizontalDistance < Math.abs(deltaY) * horizontalIntentRatio) return null;
  return deltaX < 0 ? 1 : -1;
}
